// Public content: News/Downloads/Expeditions already exist on the old backend
// (templateIDs 9/10/11). Testimonials/BlogPosts/FAQs are new (16/17/18, per
// docs/BACKEND_SPEC_MISSING_ENDPOINTS.md) and will 404 until the backend adds
// them — callers already fall back to mock content on any fetch failure.
//
// News/Downloads/Expeditions schemas below were CONFIRMED against live
// getTemplateDataList(9|10|11) data (2026-07-24) — the original schemas here
// were guessed and didn't match the real, already-registered field layout at
// all (e.g. News _1 was assumed to be Title; the real field is MeetingID).
// Real fields have no concept of sourceName/publishedDate/sortOrder (News),
// fileType/category/sortOrder (Downloads), or altitude/sortOrder
// (Expeditions) — those stay in the row/write interfaces for UI compat but
// are never read from or written to a real slot (see docs/
// BACKEND_SPEC_MISSING_ENDPOINTS.md for the fields-with-no-real-home table).
import {
  addUpdateTemplateDataWeb, cell, deleteTemplateData,
  getTemplateDataList, isApiConnected, uploadFile, type ApiEnvelope, type TemplateRow,
} from "@/lib/apiClient";

// addUpdateTemplateDataWeb resolves even when the backend rejects the write
// (hasErrors: true, response: null) — none of this app's write paths check
// that today, so a rejected write (e.g. a missing mandatory field) looks
// identical to a real success. Confirmed live: Expeditions requires
// Thumbnail/Subtitle/ImageOne and silently no-ops without them. Throw here
// so callers (and their existing try/catch → toast handling) surface it.
function assertOk(resp: ApiEnvelope<unknown>): void {
  if (resp.hasErrors) throw new Error(resp.errors || "Save failed");
}

const TEMPLATE = {
  news: 9,
  downloads: 10,
  expeditions: 11,
  testimonials: 16,
  blogPosts: 17,
  faqs: 18,
} as const;

// Read index = write "_N" + 2 (0 _id, 1 createDt, 2 templateID, 3.._1, 4.._2, ...).
// Real backend field labels in comments (confirmed live 2026-07-24).
const NEWS_SCHEMA = {
  id: 0, createDt: 1,
  refId: 3,      // MeetingID
  title: 4,      // Title
  pdf: 5,        // Pdf
  image: 6,      // Image
  audio: 7,      // Audio
  videoUrl: 8,   // VideoUrl
  sourceUrl: 9,  // LargeSizeFileUrl — holds the external article link
} as const;
const DOWNLOAD_SCHEMA = {
  id: 0, createDt: 1,
  refId: 3,        // ID
  title: 4,        // Title
  fileUrl: 5,      // Pdf — the actual downloadable file URL
  image: 6,        // Image
  audio: 7,        // Audio
  videoUrl: 8,     // VideoUrl
  largeFileUrl: 9, // LargeSizeFileUrl
} as const;
// Backend mandates Title/Thumbnail/Subtitle/ImageOne on create (confirmed
// live: "Failed! Template Data Mandatory Fields Thumbnail/Subtitle/ImageOne
// does not exists." for whichever is missing) — the admin form requires
// them accordingly (see src/pages/admin/expeditions/page.tsx).
const EXPEDITION_SCHEMA = {
  id: 0, createDt: 1,
  refId: 3,      // ID
  title: 4,      // Title
  videoUrl: 5,   // VideoUrl
  image: 6,      // Thumbnail — mandatory
  subtitle: 7,   // Subtitle — mandatory
  description: 8,// Description
  imageOne: 9,   // ImageOne — mandatory
  imageTwo: 10,  // ImageTwo (real backend label has a typo: "ImageTow")
  imageThree: 11,// ImageThree
  imageFour: 12, // ImageFour
  imageFive: 13, // ImageFive — not exposed in our UI yet
  year: 14,      // Year
} as const;
const TESTIMONIAL_SCHEMA = { id: 0, name: 1, role: 2, content: 3, rating: 4, course: 5, image: 6, featured: 7 } as const;
const BLOG_SCHEMA = {
  id: 0, slug: 1, title: 2, excerpt: 3, content: 4, image: 5, category: 6, date: 7, readTime: 8, author: 9, authorImage: 10,
} as const;
const FAQ_SCHEMA = { id: 0, question: 1, answer: 2, category: 3, sortOrder: 4 } as const;

export interface NewsEventRow { id: string; title: string; sourceUrl: string; image: string; sourceName: string; publishedDate: string; sortOrder: number }
export interface DownloadRow { id: string; title: string; fileUrl: string; fileType: string; category: string; sortOrder: number }
export interface ExpeditionRow {
  id: string; title: string; year: string; altitude: string; description: string; image: string; sortOrder: number;
  videoUrl: string; subtitle: string; imageOne: string; imageTwo: string; imageThree: string; imageFour: string;
}
export interface TestimonialRow { id: string; name: string; role: string; content: string; rating: number; course: string; image: string; featured: boolean }
export interface BlogPostRow { id: string; slug: string; title: string; excerpt: string; content: string; image: string; category: string; date: string; readTime: string; author: string; authorImage: string }
export interface FaqRow { id: string; question: string; answer: string; category: string; sortOrder: number }

async function list(templateID: number): Promise<TemplateRow[][]> {
  if (!isApiConnected()) return [];
  const { response } = await getTemplateDataList(templateID, {}, { pgSize: 500 });
  return response.records || [];
}

export async function listNewsEvents(): Promise<NewsEventRow[]> {
  const rows = await list(TEMPLATE.news);
  return rows.map((r) => ({
    id: cell(r, NEWS_SCHEMA.id), title: cell(r, NEWS_SCHEMA.title), sourceUrl: cell(r, NEWS_SCHEMA.sourceUrl),
    image: cell(r, NEWS_SCHEMA.image),
    sourceName: "", // no real field — always empty, UI should hide/omit this
    publishedDate: cell(r, NEWS_SCHEMA.createDt), // no real "published date" field; use record creation date
    sortOrder: 0, // no real field
  }));
}

export async function listDownloads(): Promise<DownloadRow[]> {
  const rows = await list(TEMPLATE.downloads);
  return rows.map((r) => ({
    id: cell(r, DOWNLOAD_SCHEMA.id), title: cell(r, DOWNLOAD_SCHEMA.title), fileUrl: cell(r, DOWNLOAD_SCHEMA.fileUrl),
    fileType: "pdf", // no real field — Pdf is the only file slot the old app uses
    category: "general", // no real field
    sortOrder: 0, // no real field
  }));
}

export async function listExpeditions(): Promise<ExpeditionRow[]> {
  const rows = await list(TEMPLATE.expeditions);
  return rows.map((r) => ({
    id: cell(r, EXPEDITION_SCHEMA.id), title: cell(r, EXPEDITION_SCHEMA.title), year: cell(r, EXPEDITION_SCHEMA.year),
    altitude: "", // no real field
    description: cell(r, EXPEDITION_SCHEMA.description),
    image: cell(r, EXPEDITION_SCHEMA.image), sortOrder: 0, // no real field
    videoUrl: cell(r, EXPEDITION_SCHEMA.videoUrl), subtitle: cell(r, EXPEDITION_SCHEMA.subtitle),
    imageOne: cell(r, EXPEDITION_SCHEMA.imageOne), imageTwo: cell(r, EXPEDITION_SCHEMA.imageTwo),
    imageThree: cell(r, EXPEDITION_SCHEMA.imageThree), imageFour: cell(r, EXPEDITION_SCHEMA.imageFour),
  }));
}

export async function listTestimonials(): Promise<TestimonialRow[]> {
  const rows = await list(TEMPLATE.testimonials);
  return rows.map((r) => ({
    id: cell(r, TESTIMONIAL_SCHEMA.id), name: cell(r, TESTIMONIAL_SCHEMA.name), role: cell(r, TESTIMONIAL_SCHEMA.role),
    content: cell(r, TESTIMONIAL_SCHEMA.content), rating: Number(cell(r, TESTIMONIAL_SCHEMA.rating) || 5),
    course: cell(r, TESTIMONIAL_SCHEMA.course), image: cell(r, TESTIMONIAL_SCHEMA.image),
    featured: cell(r, TESTIMONIAL_SCHEMA.featured) === "true",
  }));
}

export async function listBlogPosts(): Promise<BlogPostRow[]> {
  const rows = await list(TEMPLATE.blogPosts);
  return rows.map((r) => ({
    id: cell(r, BLOG_SCHEMA.id), slug: cell(r, BLOG_SCHEMA.slug), title: cell(r, BLOG_SCHEMA.title),
    excerpt: cell(r, BLOG_SCHEMA.excerpt), content: cell(r, BLOG_SCHEMA.content), image: cell(r, BLOG_SCHEMA.image),
    category: cell(r, BLOG_SCHEMA.category), date: cell(r, BLOG_SCHEMA.date), readTime: cell(r, BLOG_SCHEMA.readTime),
    author: cell(r, BLOG_SCHEMA.author), authorImage: cell(r, BLOG_SCHEMA.authorImage),
  }));
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPostRow | null> {
  const posts = await listBlogPosts();
  return posts.find((p) => p.slug === slug) || null;
}

export async function listFaqs(): Promise<FaqRow[]> {
  const rows = await list(TEMPLATE.faqs);
  return rows.map((r) => ({
    id: cell(r, FAQ_SCHEMA.id), question: cell(r, FAQ_SCHEMA.question), answer: cell(r, FAQ_SCHEMA.answer),
    category: cell(r, FAQ_SCHEMA.category) || "general", sortOrder: Number(cell(r, FAQ_SCHEMA.sortOrder) || 0),
  }));
}

// ─── Admin CRUD (News / Downloads / Expeditions) — best-effort schema ───

export async function uploadContentImage(file: File): Promise<string> {
  return uploadFile(file);
}

export type NewsEventWrite = Omit<NewsEventRow, "id">;
export type DownloadWrite = Omit<DownloadRow, "id">;
export type ExpeditionWrite = Omit<ExpeditionRow, "id">;

// CREATE must send every real field up to the highest index in use (_1.._7
// for News/Downloads, _1.._12 for Expeditions), even as "", or the backend
// omits the gap entirely and shifts every field after it left by one
// position (confirmed via the Courses/Students fixes; same backend engine).
//
// refId (_1, the real MeetingID/ID field) must be unique per the backend's
// own validation ("Failed! Template Data Fields ID not Unique."). It does
// NOT come from getMaxRecordidInTemplate — that returns an unrelated
// internal record counter (confirmed live: it returned 10 for Expeditions
// while real _1 values already ran up to "18"), so reusing it collides with
// real historical data. Instead we scan the existing rows for the highest
// numeric _1 value and use max+1 — the same approach the old app's own data
// follows (a plain incrementing serial per template).
//
// UPDATE (with _id) was confirmed to MERGE onto the existing row rather than
// replace it (see Students fix), so it only needs to send the fields the
// admin UI actually manages — resending "" for fields we don't collect
// (Pdf/Audio/VideoUrl/refId etc.) would blank out real data on every edit.
async function nextRefId(templateID: number, refIdIndex: number): Promise<string> {
  const rows = await list(templateID);
  const max = rows.reduce((m, r) => Math.max(m, Number(cell(r, refIdIndex)) || 0), 0);
  return String(max + 1);
}
function newsCreateFields(r: NewsEventWrite, refId: string): Record<string, unknown> {
  return { _1: refId, _2: r.title, _3: "", _4: r.image, _5: "", _6: "", _7: r.sourceUrl };
}
function newsUpdateFields(r: NewsEventWrite): Record<string, unknown> {
  return { _2: r.title, _4: r.image, _7: r.sourceUrl };
}

export async function createNewsEvent(r: NewsEventWrite): Promise<void> {
  const refId = await nextRefId(TEMPLATE.news, NEWS_SCHEMA.refId);
  assertOk(await addUpdateTemplateDataWeb(TEMPLATE.news, newsCreateFields(r, refId)));
}
export async function updateNewsEvent(id: string, r: NewsEventWrite): Promise<void> {
  assertOk(await addUpdateTemplateDataWeb(TEMPLATE.news, newsUpdateFields(r), id));
}
export async function deleteNewsEvents(ids: string[]): Promise<void> {
  await deleteTemplateData(TEMPLATE.news, ids);
}

function downloadCreateFields(r: DownloadWrite, refId: string): Record<string, unknown> {
  return { _1: refId, _2: r.title, _3: r.fileUrl, _4: "", _5: "", _6: "", _7: "" };
}
function downloadUpdateFields(r: DownloadWrite): Record<string, unknown> {
  return { _2: r.title, _3: r.fileUrl };
}

export async function createDownload(r: DownloadWrite): Promise<void> {
  const refId = await nextRefId(TEMPLATE.downloads, DOWNLOAD_SCHEMA.refId);
  assertOk(await addUpdateTemplateDataWeb(TEMPLATE.downloads, downloadCreateFields(r, refId)));
}
export async function updateDownload(id: string, r: DownloadWrite): Promise<void> {
  assertOk(await addUpdateTemplateDataWeb(TEMPLATE.downloads, downloadUpdateFields(r), id));
}
export async function deleteDownloads(ids: string[]): Promise<void> {
  await deleteTemplateData(TEMPLATE.downloads, ids);
}

function expeditionCreateFields(r: ExpeditionWrite, refId: string): Record<string, unknown> {
  return {
    _1: refId, _2: r.title, _3: r.videoUrl, _4: r.image, _5: r.subtitle, _6: r.description,
    _7: r.imageOne, _8: r.imageTwo, _9: r.imageThree, _10: r.imageFour, _11: "", _12: r.year,
  };
}
function expeditionUpdateFields(r: ExpeditionWrite): Record<string, unknown> {
  return {
    _2: r.title, _3: r.videoUrl, _4: r.image, _5: r.subtitle, _6: r.description,
    _7: r.imageOne, _8: r.imageTwo, _9: r.imageThree, _10: r.imageFour, _12: r.year,
  };
}

export async function createExpedition(r: ExpeditionWrite): Promise<void> {
  const refId = await nextRefId(TEMPLATE.expeditions, EXPEDITION_SCHEMA.refId);
  assertOk(await addUpdateTemplateDataWeb(TEMPLATE.expeditions, expeditionCreateFields(r, refId)));
}
export async function updateExpedition(id: string, r: ExpeditionWrite): Promise<void> {
  assertOk(await addUpdateTemplateDataWeb(TEMPLATE.expeditions, expeditionUpdateFields(r), id));
}
export async function deleteExpeditions(ids: string[]): Promise<void> {
  await deleteTemplateData(TEMPLATE.expeditions, ids);
}
